import React, { useState, useMemo } from 'react'
import { makeStyles } from '../hooks/makeStyles'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Grid,
  Chip,
} from '@mui/material'
import { generatePath, Link } from 'react-router-dom'
import { selectHasUserRole } from '../redux/reducers/UserRolesSlice'
import { useDispatch, useSelector } from 'react-redux'
import {
  DASHBOARD_ENDPOINT,
  IPD_BILLING_ENDPOINT,
  IPD_ENDPOINT,
  IPD_LAB_ORDER_ENDPOINT,
  IPD_REGISTRATION_ENDPOINT,
  IPD_ROLE_IPD_BILLING,
  IPD_ROLE_IPD_LAB_ORDER,
  IPD_ROLE_IPD_MAIN,
  IPD_ROLE_IPD_REGISTRATION,
  IPD_ROLE_IPD_VISITS,
  IPD_VISITS_ENDPOINT,
  LAB_ENDPOINT,
  LAB_MANAGEMENT_ADMIN_ROLE,
  LAB_MANAGEMENT_LAB_DEPARTMENT_ROLE,
  LAB_MANAGEMENT_LAB_TESTS_ROLE,
  LAB_MANAGEMENT_PARAMETERS_ROLE,
  LAB_MANAGEMENT_PARAMETER_RANGES_ROLE,
  LAB_MANAGEMENT_PARAMETERS_SEQUENCES_ROLE,
  LAB_MANAGEMENT_RESULT_TYPES_ROLE,
  LAB_MANAGEMENT_SAMPLE_TYPES_ROLE,
  LAB_MANAGEMENT_UNIT_TYPES_ROLE,
  LAB_MANAGEMENT_METHOD_TYPES_ROLE,
  LAB_MANAGEMENT_DEPARTMENTS_ENDPOINT,
  LAB_MANAGEMENT_ENDPOINT,
  LAB_MANAGEMENT_METHOD_TYPES_ENDPOINT,
  LAB_MANAGEMENT_PARAMETER_RANGES_ENDPOINT,
  LAB_MANAGEMENT_PARAMETER_SEQUENCES_ENDPOINT,
  LAB_MANAGEMENT_PARAMETERS_ENDPOINT,
  LAB_MANAGEMENT_RESULT_TYPES_ENDPOINT,
  LAB_MANAGEMENT_SAMPLE_TYPES_ENDPOINT,
  LAB_MANAGEMENT_TESTS_ENDPOINT,
  LAB_MANAGEMENT_UNIT_TYPES_ENDPOINT,
  LAB_ORDERS_PROCESSING,
  OPD_BILLING_ENDPOINT,
  OPD_EMERGENCY_ENDPOINT,
  OPD_ENDPOINT,
  OPD_LAB_ORDER_ENDPOINT,
  OPD_REGISTRATION_ENDPOINT,
  OPD_ROLE_OPD_BILLING,
  OPD_ROLE_OPD_EMERGENCY,
  OPD_ROLE_OPD_LAB_ORDER,
  OPD_ROLE_OPD_MAIN,
  OPD_ROLE_OPD_REGISTRATION,
  OPD_ROLE_OPD_VISITS,
  OPD_VISITS_ENDPOINT,
  ADMINISTRATION_ENDPOINT,
  ADMINISTRATION_PATHOLOGIST_ENDPOINT,
  ADMINISTRATION_B2B_PARTNER_ENDPOINT,
  ADMINISTRATION_PROCESSING_UNIT_ENDPOINT,
  ADMINISTRATION_RBAC_ENDPOINT,
  ADMINISTRATION_BED_MANAGEMENT_ENDPOINT,
  ADMINISTRATION_USER_MANAGEMENT_ENDPOINT,
  ADMIN_ROLE_ADMINISTRATION,
  ADMIN_ROLE_USER_MANAGEMENT,
  ADMIN_ROLE_PATHOLOGIST,
  ADMIN_ROLE_BED_MANAGEMENT,
  ADMIN_ROLE_ROLE_MANAGEMENT,
  ADMIN_ROLE_DASHBOARD_MAIN,
  ADMIN_ROLE_DASHBOARD_PAGE,
  LAB_ROLE_LAB_ORDERS_MAIN,
  LAB_ROLE_LAB_ORDER_PROCESSING_PAGE,
  LAB_REFERRED_OUT,
  LAB_ROLE_LAB_ORDERS_REFFERED_OUT_PAGE,
  PATIENT_INDENT_ENDPOINT,
  PATIENT_INDENT_RAISE_ENDPOINT,
  PATIENT_INDENT_RETURN_ENDPOINT,
  INDENT_ROLE_PATIENT_INDENT,
  INDENT_ROLE_PATIENT_INDENT_LIST,
  INDENT_ROLE_PATIENT_INDENT_RAISE,
  INDENT_ROLE_PATIENT_INDENT_RETURN,
  PATIENT_INDENT_LIST_ENDPOINT,
} from '../utils/Endpoints'
import SearchIcon from '@mui/icons-material/Search'
import { clearSelectedPatient } from './patient/PatientListSlice'

/**
 * Normalizes a string for case-insensitive, whitespace-insensitive comparison
 * @param str - The string to normalize
 * @returns Normalized lowercase string without extra whitespace
 */
const normalizeString = (str: string): string => {
  return str?.toLowerCase()?.trim()?.replace(/\s+/g, ' ') || ''
}

/**
 * Checks if a search term matches a target string using wildcard logic
 * Supports partial matching and word boundaries
 * @param target - The string to search in (menu item label)
 * @param searchTerm - The search query
 * @returns true if match found, false otherwise
 */
const matchesSearch = (target: string, searchTerm: string): boolean => {
  if (!searchTerm) return true // No search term means show all

  const normalizedTarget = normalizeString(target)
  const normalizedSearch = normalizeString(searchTerm)

  // Split search term by spaces to support multiple word search
  const searchWords = normalizedSearch.split(' ').filter(Boolean)

  // All search words must be found in the target (AND logic)
  return searchWords.every((word) => normalizedTarget.includes(word))
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    background: theme.palette.background.paper,
    padding: theme.spacing(2),
    gap: theme.spacing(4),
    '& .MuiListItemText-secondary': {
      fontSize: '0.75rem',
    },
    '& .MuiListItemText-primary': {
      fontSize: '0.75rem',
    },
    minWidth: '60vw',
    maxWidth: '90vw',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  header: {
    position: 'sticky',
    top: 0,
    background: theme.palette.background.paper,
    zIndex: 1,
  },
  column: {
    minWidth: 220,
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    background:
      theme.palette.mode === 'light'
        ? theme.palette.grey[200]
        : theme.palette.grey[800],
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(1),
  },
  listItem: {
    padding: theme.spacing(0.5, 1),
    '& a': {
      textDecoration: 'none',
      color: theme.palette.text.primary,
      width: '100%',
      display: 'block',
      fontSize: '0.6rem !important',
    },
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  dividerStyle: { marginY: 1 },
  newChip: {
    height: '16px',
    fontSize: '0.625rem',
    fontWeight: 600,
    '& .MuiChip-label': {
      marginTop: '-2px',
      padding: '0 6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
}))

type MenuItem = {
  label: string
  route: string
  enabled: boolean
  new?: boolean
}
type MenuSection = {
  title: string
  items: MenuItem[]
  route: string
  enabled: boolean
}

interface MegaMenuProps {
  closeMenu: () => void
}

const MegaMenu: React.FC<MegaMenuProps> = ({ closeMenu }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [searchQuery, setSearchQuery] = useState('')

  // ✅ Call selectors here

  const dashboard = useSelector(selectHasUserRole(ADMIN_ROLE_DASHBOARD_MAIN))
  const dashboardPage = useSelector(
    selectHasUserRole(ADMIN_ROLE_DASHBOARD_PAGE)
  )

  const opdFlow = useSelector(selectHasUserRole(OPD_ROLE_OPD_MAIN))

  const opdReg = useSelector(selectHasUserRole(OPD_ROLE_OPD_REGISTRATION))
  const opdVisits = useSelector(selectHasUserRole(OPD_ROLE_OPD_VISITS))
  const opdBilling = useSelector(selectHasUserRole(OPD_ROLE_OPD_BILLING))
  const opdLabOrder = useSelector(selectHasUserRole(OPD_ROLE_OPD_LAB_ORDER))
  const opdEmergency = useSelector(selectHasUserRole(OPD_ROLE_OPD_EMERGENCY))

  const ipdFlow = useSelector(selectHasUserRole(IPD_ROLE_IPD_MAIN))

  const ipdReg = useSelector(selectHasUserRole(IPD_ROLE_IPD_REGISTRATION))
  const ipdVisits = useSelector(selectHasUserRole(IPD_ROLE_IPD_VISITS))
  const ipdBilling = useSelector(selectHasUserRole(IPD_ROLE_IPD_BILLING))
  const ipdLabOrder = useSelector(selectHasUserRole(IPD_ROLE_IPD_LAB_ORDER))

  const labOrders = useSelector(selectHasUserRole(LAB_ROLE_LAB_ORDERS_MAIN))
  const labOrderProcessing = useSelector(
    selectHasUserRole(LAB_ROLE_LAB_ORDER_PROCESSING_PAGE)
  )
  const labReferredOut = useSelector(
    selectHasUserRole(LAB_ROLE_LAB_ORDERS_REFFERED_OUT_PAGE)
  )

  const labManagement = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_ADMIN_ROLE)
  )
  const labDepartments = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_LAB_DEPARTMENT_ROLE)
  )
  const labTests = useSelector(selectHasUserRole(LAB_MANAGEMENT_LAB_TESTS_ROLE))
  const labParameters = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_PARAMETERS_ROLE)
  )
  const labParameterRanges = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_PARAMETER_RANGES_ROLE)
  )
  const labParameterSequences = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_PARAMETERS_SEQUENCES_ROLE)
  )
  const labResultTypes = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_RESULT_TYPES_ROLE)
  )
  const labSampleTypes = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_SAMPLE_TYPES_ROLE)
  )
  const labUnitTypes = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_UNIT_TYPES_ROLE)
  )
  const labMethodTypes = useSelector(
    selectHasUserRole(LAB_MANAGEMENT_METHOD_TYPES_ROLE)
  )

  const administrationMenu = useSelector(
    selectHasUserRole(ADMIN_ROLE_ADMINISTRATION)
  )
  const administrationUserManagementPage = useSelector(
    selectHasUserRole(ADMIN_ROLE_USER_MANAGEMENT)
  )
  const administrationPathologistPage = useSelector(
    selectHasUserRole(ADMIN_ROLE_PATHOLOGIST)
  )
  const administrationBedManagementPage = useSelector(
    selectHasUserRole(ADMIN_ROLE_BED_MANAGEMENT)
  )
  const administrationRoleManagementPage = useSelector(
    selectHasUserRole(ADMIN_ROLE_ROLE_MANAGEMENT)
  )

  const patientIndentMenu = useSelector(
    selectHasUserRole(INDENT_ROLE_PATIENT_INDENT)
  )
  const patientIndentListPage = useSelector(
    selectHasUserRole(INDENT_ROLE_PATIENT_INDENT_LIST)
  )
  const patientIndentRaisePage = useSelector(
    selectHasUserRole(INDENT_ROLE_PATIENT_INDENT_RAISE)
  )
  const patientIndentReturnPage = useSelector(
    selectHasUserRole(INDENT_ROLE_PATIENT_INDENT_RETURN)
  )

  // Define menu data structure - memoized to prevent recreation on every render
  const menuData: MenuSection[] = useMemo(
    () => [
      {
        title: 'DASHBOARD',
        items: [
          {
            label: 'Dashboard',
            route: DASHBOARD_ENDPOINT,
            enabled: dashboardPage,
            new: true,
          },
        ],
        route: '/',
        enabled: dashboard,
      },
      {
        title: 'OP FLOW',
        items: [
          {
            label: 'Registration',
            route: OPD_REGISTRATION_ENDPOINT,
            enabled: opdReg,
          },
          { label: 'Visits', route: OPD_VISITS_ENDPOINT, enabled: opdVisits },
          {
            label: 'Billing',
            route: OPD_BILLING_ENDPOINT,
            enabled: opdBilling,
          },
          {
            label: 'Lab Orders',
            route: OPD_LAB_ORDER_ENDPOINT,
            enabled: opdLabOrder,
          },
          {
            label: 'Emergency',
            route: OPD_EMERGENCY_ENDPOINT,
            enabled: opdEmergency,
          },
        ],
        route: OPD_ENDPOINT,
        enabled: opdFlow,
      },
      {
        title: 'IP FLOW',
        items: [
          {
            label: 'Registration',
            route: IPD_REGISTRATION_ENDPOINT,
            enabled: ipdReg,
          },
          { label: 'Visits', route: IPD_VISITS_ENDPOINT, enabled: ipdVisits },
          {
            label: 'Billing',
            route: IPD_BILLING_ENDPOINT,
            enabled: ipdBilling,
          },
          {
            label: 'Lab Orders',
            route: IPD_LAB_ORDER_ENDPOINT,
            enabled: ipdLabOrder,
          },
        ],
        route: IPD_ENDPOINT,
        enabled: ipdFlow,
      },
      {
        title: 'LAB ORDERS',
        items: [
          {
            label: 'Lab Order Processing',
            route: LAB_ORDERS_PROCESSING,
            enabled: labOrderProcessing,
          },
          {
            label: 'Referred Out',
            route: LAB_REFERRED_OUT,
            enabled: labReferredOut,
          },
        ],
        route: LAB_ENDPOINT,
        enabled: labOrders,
      },
      {
        title: 'LAB MANAGEMENT',
        items: [
          {
            label: 'Lab Departments',
            route: LAB_MANAGEMENT_DEPARTMENTS_ENDPOINT,
            enabled: labDepartments,
          },
          {
            label: 'Lab Tests',
            route: LAB_MANAGEMENT_TESTS_ENDPOINT,
            enabled: labTests,
          },
          {
            label: 'Parameters',
            route: LAB_MANAGEMENT_PARAMETERS_ENDPOINT,
            enabled: labParameters,
          },
          {
            label: 'Parameters Ranges',
            route: LAB_MANAGEMENT_PARAMETER_RANGES_ENDPOINT,
            enabled: labParameterRanges,
          },
          {
            label: 'Parameters Sequences',
            route: LAB_MANAGEMENT_PARAMETER_SEQUENCES_ENDPOINT,
            enabled: labParameterSequences,
          },
          {
            label: 'Result Types',
            route: LAB_MANAGEMENT_RESULT_TYPES_ENDPOINT,
            enabled: labResultTypes,
          },
          {
            label: 'Sample Types',
            route: LAB_MANAGEMENT_SAMPLE_TYPES_ENDPOINT,
            enabled: labSampleTypes,
          },
          {
            label: 'Unit Types',
            route: LAB_MANAGEMENT_UNIT_TYPES_ENDPOINT,
            enabled: labUnitTypes,
          },
          {
            label: 'Method Types',
            route: LAB_MANAGEMENT_METHOD_TYPES_ENDPOINT,
            enabled: labMethodTypes,
          },
        ],
        route: LAB_MANAGEMENT_ENDPOINT,
        enabled: labManagement,
      },
      {
        title: 'ADMINISTRATION',
        items: [
          {
            label: 'Pathologist',
            route: ADMINISTRATION_PATHOLOGIST_ENDPOINT,
            enabled: administrationPathologistPage,
          },
          {
            label: 'B2B Partners',
            route: ADMINISTRATION_B2B_PARTNER_ENDPOINT,
            enabled: false,
          },
          {
            label: 'Processing Unit',
            route: ADMINISTRATION_PROCESSING_UNIT_ENDPOINT,
            enabled: false,
          },
          {
            label: 'Bed Management',
            route: ADMINISTRATION_BED_MANAGEMENT_ENDPOINT,
            enabled: administrationBedManagementPage,
          },
          {
            label: 'Role Management',
            route: ADMINISTRATION_RBAC_ENDPOINT,
            enabled: administrationRoleManagementPage,
          },
          {
            label: 'User Management',
            route: ADMINISTRATION_USER_MANAGEMENT_ENDPOINT,
            enabled: administrationUserManagementPage,
          },
        ],
        route: ADMINISTRATION_ENDPOINT,
        enabled: administrationMenu,
      },
      {
        title: 'PATINET INDENT',
        items: [
          {
            label: 'Patient Indent List',
            route: PATIENT_INDENT_LIST_ENDPOINT,
            enabled: patientIndentListPage,
          },
          {
            label: 'Add Patient Indent',
            route: PATIENT_INDENT_RAISE_ENDPOINT,
            enabled: patientIndentRaisePage,
          },
          {
            label: 'Add Patient Return Indent',
            route: PATIENT_INDENT_RETURN_ENDPOINT,
            enabled: patientIndentReturnPage,
          },
        ],
        route: PATIENT_INDENT_ENDPOINT,
        enabled: patientIndentMenu,
      },
    ],
    [
      dashboard,
      dashboardPage,
      opdFlow,
      opdReg,
      opdVisits,
      opdBilling,
      opdLabOrder,
      opdEmergency,
      ipdFlow,
      ipdReg,
      ipdVisits,
      ipdBilling,
      ipdLabOrder,
      labOrders,
      labOrderProcessing,
      labReferredOut,
      labManagement,
      labDepartments,
      labTests,
      labParameters,
      labParameterRanges,
      labParameterSequences,
      labResultTypes,
      labSampleTypes,
      labUnitTypes,
      labMethodTypes,
      administrationMenu,
      administrationUserManagementPage,
      administrationPathologistPage,
      administrationBedManagementPage,
      administrationRoleManagementPage,
      patientIndentMenu,
      patientIndentListPage,
      patientIndentRaisePage,
      patientIndentReturnPage,
    ]
  )

  /**
   * Filters menu sections and items based on search query
   * Returns only sections that have matching items
   */
  const filteredMenuData = useMemo(() => {
    if (!searchQuery) {
      // When no search query, filter out sections with no enabled items
      return menuData
        ?.map((section) => ({
          ...section,
          items: section?.items?.filter((item) => item.enabled),
        }))
        ?.filter((section) => section.enabled && section.items.length > 0)
    }

    return menuData
      ?.map((section) => {
        // Check if section title matches
        const sectionMatches = matchesSearch(section.title, searchQuery)

        // Filter items that match the search
        const filteredItems = section?.items?.filter(
          (item) =>
            item.enabled &&
            (sectionMatches || // If section matches, show all items
              matchesSearch(item.label, searchQuery)) // Or if item matches
        )

        // Return section only if it has matching items
        return {
          ...section,
          items: filteredItems,
        }
      })
      ?.filter(
        (section) => section.enabled && section.items.length > 0 // Only show sections with items
      )
  }, [searchQuery, menuData])

  /**
   * Handles search input change
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Grid
          container
          justifyContent='space-between'
          alignItems='center'
          pl={2}
          pr={2}
          pt={1}
          pb={1}
        >
          <Grid>
            <Typography variant='h6'>Menu</Typography>
          </Grid>
          <Grid>
            <TextField
              fullWidth
              size='small'
              placeholder='Search menu items'
              variant='outlined'
              value={searchQuery}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon fontSize='small' />,
                },
              }}
            />
          </Grid>
        </Grid>
        <Divider />
      </Box>
      <Box className={classes.root}>
        {filteredMenuData?.length === 0 ? (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            width='100%'
            py={4}
          >
            <Typography variant='body2' color='text.secondary'>
              No menu items found matching &quot;{searchQuery}&quot;
            </Typography>
          </Box>
        ) : (
          filteredMenuData?.map((section, secIndex) => (
            <Box key={secIndex} className={classes.column}>
              <Typography variant='subtitle2' className={classes.sectionTitle}>
                {section?.title}
              </Typography>

              <List disablePadding>
                {section?.items?.map((item, idx) => (
                  <ListItem key={idx} className={classes.listItem}>
                    <Link
                      to={generatePath(`${section?.route}/${item?.route}`, {
                        id: '',
                        visitId: '',
                      })}
                      onClick={() => {
                        dispatch(clearSelectedPatient())
                        sessionStorage.setItem('lastActiveTab', item?.route)
                        closeMenu()
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            display='flex'
                            alignItems='center'
                            justifyContent='space-between'
                            gap={0.5}
                          >
                            <span>{item?.label}</span>
                            {item?.new && (
                              <Chip
                                label='new'
                                size='small'
                                color='error'
                                className={classes.newChip}
                              />
                            )}
                          </Box>
                        }
                      />
                    </Link>
                  </ListItem>
                ))}
              </List>
              <Divider className={classes?.dividerStyle} />
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}

export default MegaMenu
